<template>
    <div class="border rounded-lg bg-gray-100 shadow-sm p-4">
        <b-alert :show="error" variant="danger">{{ errorMsg }}</b-alert>

        <div class="d-flex justify-content-start gap-4 align-items-center mb-3">
            <h3 class="mb-0">
                {{ category.name }}
            </h3>
            <b-badge
                v-if="externalStatusText"
                :variant="isExternalVisible ? 'soft-danger' : 'bare-secondary'"
                class="text-uppercase font-size-90"
            >
                {{ externalStatusText }}
            </b-badge>
            <b-badge
                v-if="initialLoadComplete && !canEdit"
                variant="soft-dark"
                class="text-uppercase font-size-90"
                v-b-tooltip.hover.top
                :title="$t('vendorTagCategory.lockedTooltip')"
            >
                <b-icon icon="lock-fill" variant="dark" class="mr-1"></b-icon>
                {{ $t('vendorTagCategory.locked') }}
            </b-badge>
            <!-- <b-badge 
                v-if="category.can_external_view"
                variant="soft-danger"
                class="text-uppercase font-size-90"
            >
                Visible to Vendors
            </b-badge> -->
            <!-- show not visible to vendors only if explicitly false (so we know its loaded) -->
            <!-- <b-badge 
                v-if="category.can_external_view === false"
                variant="bare-secondary"
                class="text-uppercase font-size-90"
            >
                Not Visible to Vendors
            </b-badge> -->
        </div>

        <div v-if="initialLoadComplete" key="isInitialLoaded" class="row">
            <!-- CATEGORY SETTINGS -->
            <div class="col-12 col-md-6 mt-3">
                <div class="d-flex gap-5 flex-wrap">
                    <div class="w-100">
                        <div class="hot-edit-label">{{ $t('vendorTagCategory.description') }}</div>
                        <div class="text-show-breaks">{{ category.internal_description || '---'}}</div>
                    </div>
                    <div class="w-100">
                        <div class="hot-edit-label">{{ $t('vendorTagCategory.descriptionForVendors') }}</div>
                        <div class="text-show-breaks">{{ category.external_description || '---'}}</div>
                    </div>
                    <div class="w-100">
                        <div class="hot-edit-label">{{ $t('vendorTagCategory.canVendorsView') }}</div>
                        <div class="">{{ category.can_external_view ? $t('vendorTagCategory.yes') : $t('vendorTagCategory.no')}}</div>
                    </div>
                    <div class="w-100">
                        <div class="hot-edit-label">{{ $t('vendorTagCategory.canVendorsEdit') }}</div>
                        <div class="">{{ category.can_external_select ? $t('vendorTagCategory.yes') : $t('vendorTagCategory.no')}}</div>
                    </div>
                    <div class="w-100">
                        <div class="hot-edit-label">{{ $t('vendorTagCategory.isFeatured') }}</div>
                        <div class="">{{ category.is_featured ? $t('vendorTagCategory.yes') : $t('vendorTagCategory.no')}}</div>
                    </div>
                </div>
                <div class="mt-4">
                    <b-button
                        variant="gray-primary"
                        @click="canEdit ? startEdit(category) : null"
                        :disabled="!canEdit"
                    >
                        {{ $t('vendorTagCategory.editTagCategory') }}
                    </b-button>
                </div>
                <div class="mt-4">
                    <b-button
                        variant="bare-danger"
                        @click="canEdit ? onDestroy(category) : null"
                        :disabled="!canEdit"
                    >
                        {{ $t('vendorTagCategory.deleteCategory') }}
                    </b-button>
                </div>
            </div>

            <!-- TAG LIST -->
            <div class="col-12 col-md-6">
                <div class="d-flex justify-content-between align-items-end">
                    <div class="">
                    </div>
                    <div class="mb-2">
                         <b-button
                            variant="success"
                            @click="canEdit ? startCreate('CompanyTag') : null"
                            :disabled="!canEdit"
                        >
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            {{ $t('vendorTagCategory.newTag') }}
                        </b-button>
                    </div>
                </div>
                <b-table
                    small
                    :items="tags"
                    :fields="fields"
                    primary-key="id"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :busy="loading"
                    thead-class="d-none"
                    :filter="filterString"
                    :filter-included-fields="filterIncludedFields"
                    hover
                    class="bg-white shadow border"
                    show-empty
                    :empty-text="loading ? $t('vendorTagCategory.loadingTags') : $t('vendorTagCategory.noTagsInCategory')"
                >
                    <template #cell(actions)="data">
                        <b-button-group size="sm" class="mr-2">
                            <b-button
                                v-b-tooltip.hover.top
                                :title="$t('vendorTagCategory.edit')"
                                variant="minimal-primary"
                                @click="canEdit ? startEdit(data.item) : null"
                                :disabled="!canEdit"
                            >
                                <b-icon icon="pencil"></b-icon>
                            </b-button>
                            <b-button
                                v-b-tooltip.hover.top
                                :title="$t('vendorTagCategory.delete')"
                                variant="minimal-danger"
                                @click="canEdit ? onDestroy(data.item) : null"
                                :disabled="!canEdit"
                            >
                                <b-icon icon="trash"></b-icon>
                            </b-button>
                        </b-button-group>
                    </template>
                    <template #cell(name)="data">
                            <featured-vendor-tag
                                v-if="data.item.is_featured"
                                :tag="data.item"
                                class="font-size-80"
                            ></featured-vendor-tag>
                            <span v-else class="" style="font-weight: 500">{{ data.value }}</span>                        
                    </template>
                </b-table>
                <base-table-pagination
                    v-if="totalRows"
                    :current-page.sync="currentPage"
                    :per-page.sync="perPage"
                    :total-items="totalRows"
                    :item-name="$t('vendorTagCategory.vendorTags')"
                ></base-table-pagination>
            </div>
        </div>

        <div v-else key="isInitialLoading" class="w-100 height-300">
            <busy-spinner :text="$t('vendorTagCategory.loading')"></busy-spinner>
        </div>

        <b-modal
            v-model="modalShowing"
            centered
            :title="editResourceId ? $t('vendorTagCategory.edit') : $t('vendorTagCategory.create')"
            body-class="bg-gray-100"
            footer-class="bg-gray-100"
            :ok-title="$t('buttons.save')"
            button-size="lg"
            @hidden="onModalHidden"
            @cancel="modalShowing = false"
            @ok="triggerFormSubmit"
        >
            <!-- we need a custom form for tags... at least for now -->
            <FormulateForm
                v-if="formResourceName === 'CompanyTag'"
                :name="formResourceName"
                :values="formInitialData"
                @submit="onFormSubmit"
            >
                <FormulateInput
                    type="text"
                    name="name"
                    :label="$t('vendorTagCategory.tagName')"
                />
                <FormulateInput
                    v-if="isFeaturedCategory"
                    type="badgevariantpicker"
                    name="color_variant"
                    :options="colorVariantOptions"
                    :badge-text="$t('vendorTagCategory.vendorTag')"
                    badge-class="text-uppercase font-weight-500 font-size-80 text-wrap"
                    :label="$t('vendorTagCategory.tagColor')"
                    :help="$t('vendorTagCategory.featuredTagsHelp')"
                    help-position="before"
                />
            </FormulateForm>
            <FormulateForm
                v-else
                :name="formResourceName"
                :schema="formSchema"
                :values="formInitialData"
                @submit="onFormSubmit"
            ></FormulateForm>            
        </b-modal>
    
    </div>
</template>

<script>
import { 
    //suppliosApi, 
    getResourceApi 
} from "@/js/api-service";
import { errorHandling } from '@/js/custom-vue-mixins';
import BaseTablePagination from "@/components/BaseTablePagination";
import FeaturedVendorTag from './FeaturedVendorTag.vue';
import cloneDeep from 'lodash/cloneDeep';
import invoke from 'lodash/invoke';


export default {
    props: {
        categoryId: {
            type: String,
            required: true,
        },
    },
    components: {
        BaseTablePagination,
        FeaturedVendorTag,
    },
    mixins: [
        errorHandling,
    ],
    i18n: {
        messages: {
            en: {
                vendorTagCategory: {
                    locked: 'Locked',
                    lockedTooltip: 'You do not have permission to edit this category at this time',
                    internalOnly: 'Internal Only',
                    vendorsCanEdit: 'Vendors Can Edit',
                    vendorsCanView: 'Vendors Can View',
                    description: 'Description',
                    descriptionForVendors: 'Description for Vendors',
                    canVendorsView: 'Can vendors view?',
                    canVendorsEdit: 'Can vendors edit?',
                    isFeatured: 'Is featured?',
                    yes: 'Yes',
                    no: 'No',
                    editTagCategory: 'Edit Tag Category',
                    deleteCategory: 'Delete Category',
                    newTag: 'New Tag',
                    loadingTags: 'Loading tags...',
                    noTagsInCategory: 'There are no tags in this category',
                    edit: 'Edit',
                    delete: 'Delete',
                    create: 'Create',
                    vendorTags: 'Vendor Tags',
                    loading: 'Loading...',
                    tagName: 'Tag Name',
                    vendorTag: 'Vendor Tag',
                    tagColor: 'Tag Color',
                    featuredTagsHelp: 'Featured tags display as badges throughout the app',
                    updatedToast: 'Successfully updated this item',
                    createdToast: 'Successfully created this item',
                    saveItemError: 'Error while saving this item',
                    createItemError: 'Error while creating this item',
                    schemaError: 'Could not get {name} schema',
                    blockedDeleteMsg: 'You must delete all tags in this category before you can delete it',
                    cannotDelete: 'Cannot Delete',
                    confirmDeleteTag: 'Are you sure you want to delete this Tag: {name}',
                    pleaseConfirm: 'Please Confirm',
                    confirmDeleteCategory: 'Are you SURE you want to delete this Category: {name}?  THIS CANNOT BE UNDONE.',
                    warningPleaseConfirm: 'WARNING: Please Confirm',
                    yesDelete: 'Yes, Delete',
                    noCancel: 'No, Cancel',
                    deleteError: 'Error while performing delete',
                    couldNotDelete: 'Could not delete this',
                    getCategoryError: 'Error while getting category data',
                    getTagsError: 'Error while getting tags',
                },
            },
            es: {
                vendorTagCategory: {
                    locked: 'Bloqueada',
                    lockedTooltip: 'No tiene permiso para editar esta categoría en este momento',
                    internalOnly: 'Solo Interno',
                    vendorsCanEdit: 'Los Proveedores Pueden Editar',
                    vendorsCanView: 'Los Proveedores Pueden Ver',
                    description: 'Descripción',
                    descriptionForVendors: 'Descripción para Proveedores',
                    canVendorsView: '¿Pueden verla los proveedores?',
                    canVendorsEdit: '¿Pueden editarla los proveedores?',
                    isFeatured: '¿Es destacada?',
                    yes: 'Sí',
                    no: 'No',
                    editTagCategory: 'Editar Categoría de Etiquetas',
                    deleteCategory: 'Eliminar Categoría',
                    newTag: 'Nueva Etiqueta',
                    loadingTags: 'Cargando etiquetas...',
                    noTagsInCategory: 'No hay etiquetas en esta categoría',
                    edit: 'Editar',
                    delete: 'Eliminar',
                    create: 'Crear',
                    vendorTags: 'Etiquetas de Proveedor',
                    loading: 'Cargando...',
                    tagName: 'Nombre de la Etiqueta',
                    vendorTag: 'Etiqueta de Proveedor',
                    tagColor: 'Color de la Etiqueta',
                    featuredTagsHelp: 'Las etiquetas destacadas se muestran como insignias en toda la aplicación',
                    updatedToast: 'Este elemento se actualizó correctamente',
                    createdToast: 'Este elemento se creó correctamente',
                    saveItemError: 'Error al guardar este elemento',
                    createItemError: 'Error al crear este elemento',
                    schemaError: 'No se pudo obtener el esquema de {name}',
                    blockedDeleteMsg: 'Debe eliminar todas las etiquetas de esta categoría antes de poder eliminarla',
                    cannotDelete: 'No Se Puede Eliminar',
                    confirmDeleteTag: '¿Está seguro de que desea eliminar esta Etiqueta: {name}?',
                    pleaseConfirm: 'Por Favor Confirme',
                    confirmDeleteCategory: '¿Está SEGURO de que desea eliminar esta Categoría: {name}?  ESTO NO SE PUEDE DESHACER.',
                    warningPleaseConfirm: 'ADVERTENCIA: Por Favor Confirme',
                    yesDelete: 'Sí, Eliminar',
                    noCancel: 'No, Cancelar',
                    deleteError: 'Error al realizar la eliminación',
                    couldNotDelete: 'No se pudo eliminar',
                    getCategoryError: 'Error al obtener los datos de la categoría',
                    getTagsError: 'Error al obtener las etiquetas',
                },
            },
            pt: {
                vendorTagCategory: {
                    locked: 'Bloqueada',
                    lockedTooltip: 'Você não tem permissão para editar esta categoria neste momento',
                    internalOnly: 'Apenas Interno',
                    vendorsCanEdit: 'Fornecedores Podem Editar',
                    vendorsCanView: 'Fornecedores Podem Visualizar',
                    description: 'Descrição',
                    descriptionForVendors: 'Descrição para Fornecedores',
                    canVendorsView: 'Os fornecedores podem visualizar?',
                    canVendorsEdit: 'Os fornecedores podem editar?',
                    isFeatured: 'É destacada?',
                    yes: 'Sim',
                    no: 'Não',
                    editTagCategory: 'Editar Categoria de Etiquetas',
                    deleteCategory: 'Excluir Categoria',
                    newTag: 'Nova Etiqueta',
                    loadingTags: 'Carregando etiquetas...',
                    noTagsInCategory: 'Não há etiquetas nesta categoria',
                    edit: 'Editar',
                    delete: 'Excluir',
                    create: 'Criar',
                    vendorTags: 'Etiquetas de Fornecedor',
                    loading: 'Carregando...',
                    tagName: 'Nome da Etiqueta',
                    vendorTag: 'Etiqueta de Fornecedor',
                    tagColor: 'Cor da Etiqueta',
                    featuredTagsHelp: 'Etiquetas em destaque são exibidas como emblemas em todo o aplicativo',
                    updatedToast: 'Item atualizado com sucesso',
                    createdToast: 'Item criado com sucesso',
                    saveItemError: 'Erro ao salvar este item',
                    createItemError: 'Erro ao criar este item',
                    schemaError: 'Não foi possível obter o esquema de {name}',
                    blockedDeleteMsg: 'Você deve excluir todas as etiquetas desta categoria antes de poder excluí-la',
                    cannotDelete: 'Não É Possível Excluir',
                    confirmDeleteTag: 'Tem certeza de que deseja excluir esta Etiqueta: {name}?',
                    pleaseConfirm: 'Por Favor, Confirme',
                    confirmDeleteCategory: 'Tem CERTEZA de que deseja excluir esta Categoria: {name}?  ISSO NÃO PODE SER DESFEITO.',
                    warningPleaseConfirm: 'AVISO: Por Favor, Confirme',
                    yesDelete: 'Sim, Excluir',
                    noCancel: 'Não, Cancelar',
                    deleteError: 'Erro ao executar a exclusão',
                    couldNotDelete: 'Não foi possível excluir',
                    getCategoryError: 'Erro ao obter os dados da categoria',
                    getTagsError: 'Erro ao obter as etiquetas',
                },
            },
            'zh-Hans': {
                vendorTagCategory: {
                    locked: '已锁定',
                    lockedTooltip: '您目前没有权限编辑此类别',
                    internalOnly: '仅限内部',
                    vendorsCanEdit: '供应商可编辑',
                    vendorsCanView: '供应商可查看',
                    description: '描述',
                    descriptionForVendors: '向供应商显示的描述',
                    canVendorsView: '供应商可以查看吗？',
                    canVendorsEdit: '供应商可以编辑吗？',
                    isFeatured: '是否精选？',
                    yes: '是',
                    no: '否',
                    editTagCategory: '编辑标签类别',
                    deleteCategory: '删除类别',
                    newTag: '新建标签',
                    loadingTags: '正在加载标签...',
                    noTagsInCategory: '此类别中没有标签',
                    edit: '编辑',
                    delete: '删除',
                    create: '创建',
                    vendorTags: '供应商标签',
                    loading: '加载中...',
                    tagName: '标签名称',
                    vendorTag: '供应商标签',
                    tagColor: '标签颜色',
                    featuredTagsHelp: '精选标签会在整个应用中显示为徽章',
                    updatedToast: '已成功更新此项目',
                    createdToast: '已成功创建此项目',
                    saveItemError: '保存此项目时出错',
                    createItemError: '创建此项目时出错',
                    schemaError: '无法获取 {name} 架构',
                    blockedDeleteMsg: '必须先删除此类别中的所有标签，然后才能删除该类别',
                    cannotDelete: '无法删除',
                    confirmDeleteTag: '您确定要删除此标签吗：{name}',
                    pleaseConfirm: '请确认',
                    confirmDeleteCategory: '您真的确定要删除此类别吗：{name}？此操作无法撤销。',
                    warningPleaseConfirm: '警告：请确认',
                    yesDelete: '是，删除',
                    noCancel: '否，取消',
                    deleteError: '执行删除时出错',
                    couldNotDelete: '无法删除此项',
                    getCategoryError: '获取类别数据时出错',
                    getTagsError: '获取标签时出错',
                },
            },
            'zh-Hant': {
                vendorTagCategory: {
                    locked: '已鎖定',
                    lockedTooltip: '您目前沒有權限編輯此類別',
                    internalOnly: '僅限內部',
                    vendorsCanEdit: '供應商可編輯',
                    vendorsCanView: '供應商可檢視',
                    description: '描述',
                    descriptionForVendors: '向供應商顯示的描述',
                    canVendorsView: '供應商可以檢視嗎？',
                    canVendorsEdit: '供應商可以編輯嗎？',
                    isFeatured: '是否精選？',
                    yes: '是',
                    no: '否',
                    editTagCategory: '編輯標籤類別',
                    deleteCategory: '刪除類別',
                    newTag: '新增標籤',
                    loadingTags: '正在載入標籤...',
                    noTagsInCategory: '此類別中沒有標籤',
                    edit: '編輯',
                    delete: '刪除',
                    create: '建立',
                    vendorTags: '供應商標籤',
                    loading: '載入中...',
                    tagName: '標籤名稱',
                    vendorTag: '供應商標籤',
                    tagColor: '標籤顏色',
                    featuredTagsHelp: '精選標籤會在整個應用程式中顯示為徽章',
                    updatedToast: '已成功更新此項目',
                    createdToast: '已成功建立此項目',
                    saveItemError: '儲存此項目時發生錯誤',
                    createItemError: '建立此項目時發生錯誤',
                    schemaError: '無法取得 {name} 結構描述',
                    blockedDeleteMsg: '必須先刪除此類別中的所有標籤，才能刪除該類別',
                    cannotDelete: '無法刪除',
                    confirmDeleteTag: '您確定要刪除此標籤嗎：{name}',
                    pleaseConfirm: '請確認',
                    confirmDeleteCategory: '您真的確定要刪除此類別嗎：{name}？此操作無法復原。',
                    warningPleaseConfirm: '警告：請確認',
                    yesDelete: '是，刪除',
                    noCancel: '否，取消',
                    deleteError: '執行刪除時發生錯誤',
                    couldNotDelete: '無法刪除此項目',
                    getCategoryError: '取得類別資料時發生錯誤',
                    getTagsError: '取得標籤時發生錯誤',
                },
            },
        },
    },
    data: function () {
        return {
            itemTitle: "Vendor Tags",
            errorMsg: "Error",
            error: false,
            initialLoadComplete: false,  // set false on first category load
            loadingCategory: false,
            loadingTags: false,
            saving: false,
            category: {},  // category object
            tags: [],  // list of tag objects
            schemas: {}, // when you get schemas for different objects, set here by their resourceName
            fields: [  // cols to show in b-table
                {
                    key: "name",
                    tdClass: "text-left align-middle pl-3",
                    
                },{
                    key: "actions",
                    tdClass: "text-right align-middle",
                },
            ], 
            filterString: "", // feed this into b-table filter prop, v-model from filter input box
            filterIncludedFields: ["name", "context"], // filter is applied to these cols in b-table
            currentPage: 1,
            perPage: 25,
            // CRUD MODAL/FORM
            formName: 'tagForm',  // no need to reset this... just here to reference from multiple places
            modalShowing: false,
            editResourceId: '',  // empty for creates, has ID for edits
            formResourceName: '',  // set to the active resource you are editing/creating
            //formData: {},  // v-model - live data in form - actually, not even needed here - just set initialData and let form manage its own data, get it in submit handler
            formInitialData: {},  // used in :values prop of form, only for INITIAL data
            formSchema: [],  // set it when you start something
        };
    },
    mounted() {
        this.getCategory();  // get the CompanyTagCategory itself
        this.getTags();  // get the CompanyTag list in this category
        this.getCategorySchema();
        this.getTagSchema();
    },
    computed: {
        colorVariantOptions() {
            // hard-coding for now - get from backend later
            const colors = [ 
                'red', 'orange', 'yellow', 'green', 'teal', 'cyan', 
                'blue', 'indigo', 'purple', 'pink', 'light', 'dark', 'secondary',
            ];
            // we want soft ones all together... looks better
            const variants = [];
            colors.forEach(c => variants.push(c));
            colors.forEach(c => variants.push(`soft-${c}`));
            return variants;
        },
        loading() {
            return this.loadingCategory || this.loadingTags;
        },
        categoryApi() {
            //return suppliosApi.companyTagCategory;
            return getResourceApi('CompanyTagCategory');
        },
        tagApi() {
            return getResourceApi('CompanyTag');
        },
        totalRows() {
            return this.tags.length;
        },
        isFeaturedCategory() {
            return this.category?.is_featured;
        },
        hasTags() {
            return !!this.tags && Array.isArray(this.tags) && this.tags.length;
        },
        isExternalVisible() {
            // can vendors edit OR view?  used to show different badge colors on things
            return this.category?.can_external_view || this.category?.can_external_select;
        },
        externalStatusText() {
            // return something like 'Vendors Can Edit' or 'Vendors Can View"
            if (!this.category?.id) {
                return '';  // if we don't have category yet
            }
            if (this.category.can_external_select === false && this.category.can_external_view === false) {
                // checking if they are both explicitly false, to avoid incorrectly saying something is internal only
                return this.$t('vendorTagCategory.internalOnly');
            } else if (this.category.can_external_select) {
                return this.$t('vendorTagCategory.vendorsCanEdit');
            } else if (this.category.can_external_view) {
                return this.$t('vendorTagCategory.vendorsCanView');
            }
            return '';  // final default to make linter happy
        },
        canEdit() {
            // Returns true if the current user can edit this category
            return !!this.category.can_tu_edit;
        },
    },
    methods: {
        triggerFormSubmit() {
            // called from the modal 'ok' button - submit form programmatically
            // we have 2 forms, but only one at a time, form name is this.formResourceName
            this.$formulate.submit(this.formResourceName);
        },
        async onFormSubmit(submitData) {
            // called from @submit of the form - do the submit, return promise
            console.log('START OF onFormSubmit', submitData);
            this.saving = true;
            let resp;
            const api = getResourceApi(this.formResourceName);
            const isEdit = !!this.editResourceId;
            const isTag = this.formResourceName === 'CompanyTag';
            const isCategory = this.formResourceName === 'CompanyTagCategory';
            const extraSubmitData = isTag ? {category_id: this.categoryId} : {};
            const finalData = Object.assign(submitData, extraSubmitData);
            //const apiAction = isEdit ? api.partialUpdate : api.create;
            const apiActionName = isEdit ? 'partialUpdate' : 'create';
            const actionArgs = isEdit ? [this.editResourceId, finalData] : [finalData];
            console.log('finalData', finalData);
            console.log('apiActionName', apiActionName);
            console.log('actionArgs', actionArgs);
            try {
                // NOPE resp = await apiAction(...actionArgs);
                resp = await invoke(api, apiActionName, ...actionArgs);
                console.log(resp.data);
                this.$toast.success(isEdit ? this.$t('vendorTagCategory.updatedToast') : this.$t('vendorTagCategory.createdToast'));
            } catch (error) {
                this.onError(error, isEdit ? this.$t('vendorTagCategory.saveItemError') : this.$t('vendorTagCategory.createItemError'));
            }
            // success message / toast
            this.modalShowing = false;  // close modal - which will reset form on @hidden
            // REFRESH THINGS
            if (isCategory) {
                this.getCategory();
                // SHOULD reload even though we get the data returned...
                // because we force some things on/off in save() method in backend
            }
            this.getTags();  // do this if we updated category OR tags
            this.saving = false;
        },
        onModalHidden() {
            this.resetForm();
        },
        resetForm() {
            this.editResourceId = '';
            this.formResourceName = '';
            //this.formData = {};
            this.formInitialData = {};
            this.formSchema = [];
        },
        async startCreate(resourceName) {
            console.log('Start Create', resourceName);
            const schema = await this.getSchema(resourceName);
            this.openModal({resourceName, schema, data: {}, editResourceId: ''});
        },
        async startEdit(resource) {
            console.log('Start Edit', resource);
            const resourceName = resource.model_class;
            const schema = await this.getSchema(resourceName);
            this.openModal({resourceName, schema, data: resource, editResourceId: resource.id});
        },
        openModal({resourceName, editResourceId, data, schema}) {
            // generic way to start create/edit
            // set things first, then open modal
            this.formResourceName = resourceName;
            this.editResourceId = editResourceId ?? '';
            this.formInitialData = cloneDeep(data);
            this.formSchema = cloneDeep(schema);
            // finally, open the modal
            this.modalShowing = true;
        },
        async getCategorySchema() {
            return await this.getSchema('CompanyTagCategory');
        },
        async getTagSchema() {
            return await this.getSchema('CompanyTag');
        },
        async getSchema(resourceName) {
            // fetch the schema for resourceName -- unless you already have it
            const existing = this.schemas[resourceName];
            if (existing) {
                return existing;
            }
            const api = getResourceApi(resourceName);  // returns the api endpoint object for this resource - has all methods on it
            // only works for endpoints that use 'ResourceViewsetEndpoint'
            let response;
            try {
                response = await api.getSchema();
            } catch (error) {
                this.onError(error, this.$t('vendorTagCategory.schemaError', { name: resourceName }));
            }
            const rawSchema = response.data;
            // MODIFY SCHEMA HERE IF NEEDED
            // if (resourceName === 'CompanyTag') {
            //     const schema = rawSchema.forEach(fld => {
            //         if (fld.name === 'color_variant') {
                            // Later... if needed... this is annoying... just declare the damn form here 
            //         }
            //     })
            // }

            // stash it in our 'schema store'
            this.schemas[resourceName] = rawSchema;
            return this.schemas[resourceName];
        },
        onDestroy(itemObj) {
            const resourceName = itemObj.model_class;
            const isTag = resourceName === 'CompanyTag';
            const isCategory = resourceName === 'CompanyTagCategory';
            if (isCategory && this.hasTags) {
                // backend currently does not allow delete if there are tags
                // so tell user to delete tags first (annoying, but safe)
                const blockedMsg = this.$t('vendorTagCategory.blockedDeleteMsg');
                const blockedOk = this.$bvModal.msgBoxOk(blockedMsg, {
                        title: this.$t('vendorTagCategory.cannotDelete'),
                        size: "sm",
                        buttonSize: "sm",
                        centered: true,
                    })
                    .then(value => {
                        // just bail, no further action
                        return value;
                    });
                return blockedOk;
            }
            //const resourceFriendlyName = isTag ? 'Vendor Tag' : 'Category';
            let msg;
            let title;
            let headerBgVariant;
            let headerTextVariant;
            if (isTag) {
                msg = this.$t('vendorTagCategory.confirmDeleteTag', { name: itemObj.name });
                title = this.$t('vendorTagCategory.pleaseConfirm');
                headerBgVariant = 'light';
                headerTextVariant = 'dark';
            } else {
                // if a CATEGORY, give extra warning
                msg = this.$t('vendorTagCategory.confirmDeleteCategory', { name: itemObj.name });
                title = this.$t('vendorTagCategory.warningPleaseConfirm');
                headerBgVariant = 'danger';
                headerTextVariant = 'light';
            }
            this.$bvModal
                .msgBoxConfirm(msg, {
                        title: title,
                        size: "sm",
                        buttonSize: "sm",
                        okVariant: "danger",
                        okTitle: this.$t('vendorTagCategory.yesDelete'),
                        cancelTitle: this.$t('vendorTagCategory.noCancel'),
                        footerClass: "p-2",
                        headerBgVariant: headerBgVariant,
                        headerTextVariant: headerTextVariant,
                        hideHeaderClose: false,
                        centered: true,
                    }
                )
                .then((value) => {
                    // value resolves to true if OK is clicked, false if cancel clicked, or null if modal closed
                    if (value === true) {
                        this.performDelete(itemObj);
                        console.log(
                            "From delete confirm modal, we just called performDelete"
                        );
                    }
                })
                //.catch(error => this.handleError(error, 'Error while performing delete'))
                .catch((error) =>
                    this.onError(error, this.$t('vendorTagCategory.deleteError'))
                );
        },
        async performDelete(itemObj) {
            const resourceName = itemObj.model_class;
            const api = getResourceApi(resourceName);
            let resp;
            try {
                resp = await api.destroy(itemObj.id);
                console.log(`DELETED`, itemObj, resp.data);
            } catch (error) {
                this.onError(error, this.$t('vendorTagCategory.couldNotDelete'));
            }
            if (resourceName === 'CompanyTag') {
                this.getTags();
            } else {
                // otherwise, we deleted the CATEGORY - need to emit to parent
                this.$emit('deleted');
            }
        },
        async getCategory() {
            // fetch the category data we are managing, no tags
            this.loadingCategory = true;
            let response;
            try {
                //response = await suppliosApi.companyTagCategory.retrieve(this.categoryId);
                response = await this.categoryApi.retrieve(this.categoryId);
            } catch (error) {
                this.onError(error, this.$t('vendorTagCategory.getCategoryError'));
            }
            this.category = Object.assign({}, response.data);  // merge with existing? maybe on save
            this.loadingCategory = false;
            this.initialLoadComplete = true;
            this.$emit("loaded-category", this.category);
        },
        async getTags() {
            // get list of tags just for this category
            this.loadingTags = true;
            let response;
            try {
                //response = await suppliosApi.companyTagCategory.get_tags(this.categoryId);
                response = await this.categoryApi.get_tags(this.categoryId);
            } catch (error) {
                this.onError(error, this.$t('vendorTagCategory.getTagsError'));
            }
            this.tags = response.data;
            this.loadingTags = false;
            this.$emit("loaded-tags", this.tags);
        },
        onError(error, message) {
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        clearError() {
            // called from error alert close button
            this.error = false;
            this.errorMsg = '';
        },
    },
};
</script>
